import { useState } from "react";
import { useNavigate } from "react-router";
import TextInputFloat from "../../common/components/textInput/TextInputFloat";
import Logo from "../../common/components/Logo";
import login_Service from "../../../api/services/login_Service";
import { playSong } from "../../common/actions/common.actions";
import { useDispatch, useSelector } from "react-redux";
import { selectLoginData } from "../login/login.actions";
import Modal from "../../common/components/modal";
import { IoIosArrowBack } from "react-icons/io";
import { useTranslation } from "react-i18next";

const ChangePassword = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState("Change Password Success");
  const navigate = useNavigate();
  const { userDetails } = useSelector(selectLoginData);
  const { t } = useTranslation();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (oldPassword && newPassword && confirmPassword) {
      if (newPassword === confirmPassword) {
        try {
          setIsLoading(true);
          await login_Service.changePassword({
            userId: Number(userDetails.userId),
            CurrentPassword: oldPassword,
            NewPassword: newPassword,
            ConfirmPassword: confirmPassword,
          });
          setIsSuccess(true);
          setMessage("Password changed successfully!");
        } catch (error) {
          setIsSuccess(false);
          setMessage("Change password failed!");
          console.error("API error:", error);
        } finally {
          setIsLoading(false);
          setIsModalOpen(true);
          setOldPassword("");
          setNewPassword("");
          setConfirmPassword("");
        }
      } else {
        setIsSuccess(false);
        setMessage("New and confirm password must match.");
        setIsModalOpen(true);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    if (isSuccess) navigate("/dashboard");
  };

  const handleBackClick = () => {
    dispatch(playSong());
    navigate("/setting");
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-gradient-to-br from-[#EAEAEA] via-green-50 to-[#228B22]/10 relative font-dangrek">
      {/* Football Background Animation */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        {[...Array(6)].map((_, i) => (
          <div
            key={`password-line-${i}`}
            className="absolute w-full h-px bg-[#228B22] opacity-30"
            style={{ top: `${(i + 1) * 16.66}%` }}
          />
        ))}

        {/* Floating Football and Money Icons */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`password-icon-${i}`}
            className="absolute text-lg opacity-10"
            style={{
              left: `${10 + i * 12}%`,
              top: `${20 + (i % 3) * 25}%`,
              color: i % 3 === 0 ? '#228B22' : i % 3 === 1 ? '#FFD700' : '#1E90FF',
              animation: `floatingPasswordIcon 8s ease-in-out infinite ${i * 1.5}s`
            }}
          >
            {i % 3 === 0 ? '⚽' : i % 3 === 1 ? '🔒' : '$'}
          </div>
        ))}
      </div>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes floatingPasswordIcon {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-15px) scale(1.1); }
        }
      `}</style>

      {/* Header */}
      <div className="relative z-10 bg-gradient-to-r from-[#228B22] via-emerald-600 to-[#1F5F1F] py-6 px-4 shadow-lg backdrop-blur-sm">
        <div className="flex items-center">
          <button
            className="w-10 h-10 mr-4 flex justify-center items-center text-white hover:bg-white/20 rounded-xl transition-all duration-200"
            onClick={handleBackClick}
          >
            <IoIosArrowBack size={25} />
          </button>
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3">
            <span className="text-white text-lg">🔒</span>
          </div>
          <div>
            <h2 className="text-white font-bold text-xl">{t("changePassword")}</h2>
            <p className="text-emerald-100 text-xs font-medium">Update Your Password</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-4">
        {/* Logo Section */}
        <div className="mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 border border-[#228B22]/20 shadow-lg">
            <Logo />
          </div>
        </div>

        {/* Change Password Form */}
        <div className="w-full max-w-md">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-[#228B22]/20 shadow-lg">
            {/* Form Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#228B22] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🔒</span>
              </div>
              <h3 className="text-[#228B22] font-bold text-xl mb-2">Secure Password Change</h3>
              <p className="text-gray-600 text-sm">Enter your current password and new password</p>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-6">
              {/* Old Password */}
              <div>
                <label className="flex items-center text-sm font-bold text-[#228B22] mb-2">
                  <span className="w-2 h-2 bg-[#228B22] rounded-full mr-2"></span>
                  Current Password
                </label>
                <TextInputFloat
                  id="oldPassword"
                  placeholder="Enter current password"
                  type="password"
                  value={oldPassword}
                  setValue={setOldPassword}
                />
              </div>

              {/* New Password */}
              <div>
                <label className="flex items-center text-sm font-bold text-[#228B22] mb-2">
                  <span className="w-2 h-2 bg-[#FFD700] rounded-full mr-2"></span>
                  New Password
                </label>
                <TextInputFloat
                  id="newPassword"
                  placeholder="Enter new password"
                  type="password"
                  value={newPassword}
                  setValue={setNewPassword}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="flex items-center text-sm font-bold text-[#228B22] mb-2">
                  <span className="w-2 h-2 bg-[#1E90FF] rounded-full mr-2"></span>
                  Confirm Password
                </label>
                <TextInputFloat
                  id="confirmPassword"
                  placeholder="Confirm new password"
                  type="password"
                  value={confirmPassword}
                  setValue={setConfirmPassword}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full relative group overflow-hidden bg-gradient-to-r from-[#FFD700] via-yellow-400 to-[#FFD700] p-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 mt-6"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-[#FFD700] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center space-x-3">
                  <span className="text-gray-900 font-bold text-lg">Update Password</span>
                  <span className="text-gray-900 text-lg">🔒</span>
                </div>
              </button>
            </form>

            {/* Security Note */}
            <div className="mt-6 p-3 bg-[#228B22]/10 rounded-xl border border-[#228B22]/20">
              <div className="flex items-center">
                <span className="w-2 h-2 bg-[#228B22] rounded-full mr-2 animate-pulse"></span>
                <span className="text-xs text-gray-600 font-medium">Your password is encrypted and secure</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <Modal title="777 Soccer" content={message} onClose={closeModal} />
      )}
    </div>
  );
};

export default ChangePassword;
